import { useEffect, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import {
  BasketsDataSource,
  CreateBasketPayload,
} from '../../../../datasources/baskets/BasketsDataSource';
import {
  getApiErrorMessage,
  getGenericErrorMessage,
} from '../../../../datasources/GenericErrors';
import { Basket } from '../../../../types/basket/Basket';
import { BasketListScreen } from '../shared/BasketListScreen';
import { BasketTable, BasketTableColumn } from '../shared/BasketTable';
import { useTextSearch } from '../shared/useTextSearch';
import { CreateBasketDialog } from './createBasketDialog';
import { EditBasketDialog } from './editBasketDialog';

const MAX_TOOLTIP_PRODUCTS = 10;

const COLUMNS: BasketTableColumn<Basket>[] = [
  {
    id: 'basket',
    label: 'Cesta',
    minWidth: 240,
    render: (basket) => (
      <>
        <Typography variant="body2" fontWeight={700}>
          {basket.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {basket.code}
        </Typography>
      </>
    ),
  },
  {
    id: 'allowModelSelector',
    label: 'Especificar modelo',
    minWidth: 150,
    render: (basket) => (basket.allow_model_selector ? 'Sim' : 'Não'),
  },
  {
    id: 'products',
    label: 'Produtos',
    minWidth: 110,
    render: (basket) => {
      const items = basket.basket_items ?? [];
      const lines = items
        .slice(0, MAX_TOOLTIP_PRODUCTS)
        .map(
          (item) =>
            `${item.product_sap_code} - ${item.product?.description ?? '—'}`
        );
      if (items.length > MAX_TOOLTIP_PRODUCTS) {
        lines.push(`+ ${items.length - MAX_TOOLTIP_PRODUCTS} produto(s)`);
      }

      return (
        <Tooltip
          title={
            <>
              {lines.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </>
          }
        >
          <span>{items.length}</span>
        </Tooltip>
      );
    },
  },
];

export const BasketPage = () => {
  const datasource = BasketsDataSource;

  // const [deleteDialog, setDeleteDialog] = useState<>();
  const [createDialog, setCreateDialog] = useState(false);
  const [editingBasket, setEditingBasket] = useState<Basket | null>(null);

  const [baskets, setBaskets] = useState<Basket[]>([]);
  const [savingBasket, setSavingBasket] = useState(false);
  const [loadingBaskets, setLoadingBaskets] = useState(true);
  const [createError, setCreateError] = useState<string>();
  const [editError, setEditError] = useState<string>();
  const [listError, setListError] = useState<string>();
  const [listRetry, setListRetry] = useState(0);
  const [deletingBasketId, setDeletingBasketId] = useState<number>();

  useEffect(() => {
    let cancelled = false;
    setLoadingBaskets(true);

    const loadData = async () => {
      try {
        const data = await datasource.getAll();
        if (!cancelled) {
          setBaskets(data);
          setListError(undefined);
        }
      } catch (error) {
        if (!cancelled) {
          setListError(
            getGenericErrorMessage(
              error,
              'Não foi possível carregar os balaios.'
            )
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingBaskets(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [listRetry]);

  async function handleCreateBasket(
    payload: CreateBasketPayload
  ): Promise<void> {
    setSavingBasket(true);
    setCreateError(undefined);

    try {
      await datasource.createBasket(payload);
    } catch (error) {
      setCreateError(
        getApiErrorMessage(error, 'Não foi possível criar o balaio.')
      );
      setSavingBasket(false);
      return;
    }

    setCreateDialog(false);
    setSavingBasket(false);

    try {
      setBaskets(await datasource.getAll());
      setListError(undefined);
    } catch (error) {
      setListError(
        `Balaio criado, mas ${getGenericErrorMessage(
          error,
          'não foi possível atualizar a lista.'
        )}`
      );
    }
  }

  async function handleUpdateBasket(
    basketId: number,
    payload: CreateBasketPayload
  ): Promise<void> {
    setSavingBasket(true);
    setEditError(undefined);

    try {
      await datasource.updateBasket(basketId, payload);
    } catch (error) {
      setEditError(
        getApiErrorMessage(error, 'Não foi possível atualizar o balaio.')
      );
      setSavingBasket(false);
      return;
    }

    setEditingBasket(null);
    setSavingBasket(false);

    try {
      setBaskets(await datasource.getAll());
      setListError(undefined);
    } catch (error) {
      setListError(
        `Balaio atualizado, mas ${getGenericErrorMessage(
          error,
          'não foi possível atualizar a lista.'
        )}`
      );
    }
  }

  async function handleDeleteBasket(basket: Basket): Promise<void> {
    if (
      !window.confirm(`Deseja realmente excluir o balaio "${basket.name}"?`)
    ) {
      return;
    }

    setDeletingBasketId(basket.id);

    try {
      await datasource.delete(basket.id);
      setBaskets((currentBaskets) =>
        currentBaskets.filter((item) => item.id !== basket.id)
      );
      setListError(undefined);
    } catch (error) {
      setListError(
        getApiErrorMessage(error, 'Não foi possível excluir o balaio.')
      );
    } finally {
      setDeletingBasketId(undefined);
    }
  }

  return (
    <>
      <BasketListScreen
        title="Cesta de balaios"
        description="Gerencie as cestas de produtos exibidas nas respostas das perguntas."
        actionLabel="Nova cesta"
        onAction={() => {
          setCreateError(undefined);
          setCreateDialog(true);
        }}
        searchPlaceholder="Buscar por nome, código ou produto..."
        loading={loadingBaskets}
        loadingLabel="Carregando balaios"
        error={listError}
        onRetry={() => setListRetry((attempt) => attempt + 1)}
      >
        {(search) => (
          <BasketsTable
            baskets={baskets}
            search={search}
            deletingBasketId={deletingBasketId}
            onEdit={(basket) => {
              setEditError(undefined);
              setEditingBasket(basket);
            }}
            onDelete={(basket) => void handleDeleteBasket(basket)}
          />
        )}
      </BasketListScreen>

      <CreateBasketDialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        loading={savingBasket}
        error={createError}
        canConfirm={true}
        onConfirm={handleCreateBasket}
      />
      {editingBasket && (
        <EditBasketDialog
          open={Boolean(editingBasket)}
          basket={editingBasket}
          onClose={() => setEditingBasket(null)}
          loading={savingBasket}
          error={editError}
          onConfirm={handleUpdateBasket}
        />
      )}
    </>
  );
};

type BasketsTableProps = {
  baskets: Basket[];
  search: string;
  deletingBasketId?: number;
  onEdit: (basket: Basket) => void;
  onDelete: (basket: Basket) => void;
};

const BasketsTable = ({
  baskets,
  search,
  deletingBasketId,
  onEdit,
  onDelete,
}: BasketsTableProps) => {
  const rows = useTextSearch(baskets, search, (basket) => [
    basket.name,
    basket.code,
    ...(basket.basket_items ?? []).flatMap((item) => [
      item.product_sap_code,
      item.product?.description,
    ]),
  ]);

  return (
    <BasketTable
      columns={COLUMNS}
      rows={rows}
      getRowId={(basket) => basket.id}
      getRowLabel={(basket) => `cesta ${basket.name}`}
      itemsLabel="cestas"
      emptyMessage="Nenhuma cesta cadastrada."
      searching={search.trim() !== ''}
      resetKey={search}
      onEdit={onEdit}
      editLabel="Editar"
      menuActions={[
        {
          label: 'Excluir',
          icon: <DeleteOutline fontSize="small" />,
          destructive: true,
          disabled: (basket) => deletingBasketId === basket.id,
          onClick: onDelete,
        },
      ]}
    />
  );
};
